import React from 'react';
import { ScanBatch } from '../../types/scan';
import { Spinner } from '../../design-system';
import { useScanBatchProgress } from '../../hooks/useScanBatchProgress';
import { SCAN_TYPE_NAMES, formatDateCompact, formatRunningStatus } from './scanBatchDisplay';
import ScanBranchCell from './ScanBranchCell';

interface ScanBatchRunningRowProps {
  batch: ScanBatch;
  applicationName: string;
  /** Show the repository name under the scan type — only useful when the list spans applications. */
  showApplicationName?: boolean;
}

/**
 * An in-progress scan batch as a table row. Sits in the same table as completed
 * scans; the findings column carries live progress instead of severity pills,
 * since the findings are still arriving.
 */
const ScanBatchRunningRow: React.FC<ScanBatchRunningRowProps> = ({
  batch,
  applicationName,
  showApplicationName = false,
}) => {
  const scanType = SCAN_TYPE_NAMES[batch.scan_type] || 'Security Scan';
  const { percentComplete, status } = useScanBatchProgress(batch.id);

  // Until the first progress poll returns we have no percentage, so fall back to the
  // batch's own status so the cell never looks empty.
  const hasProgress = status !== null;

  return (
    <tr className="bg-brand-primary/[0.03]">
      <td className="py-3.5 px-4 align-middle">
        <div className="font-semibold text-ui-text-primary">{scanType}</div>
        {showApplicationName && <div className="text-xs text-ui-text-tertiary mt-0.5">{applicationName}</div>}
      </td>

      <td className="py-3.5 px-4 align-middle">
        <span className="inline-flex items-center gap-2 text-sm text-ui-text-secondary whitespace-nowrap">
          <Spinner size={14} tone="current" />
          {hasProgress ? (
            <>
              Scan running
              <span className="font-semibold text-brand-primary tabular-nums">{percentComplete}%</span>
            </>
          ) : (
            formatRunningStatus(batch.status)
          )}
        </span>
      </td>

      <ScanBranchCell branches={batch.branches} />

      <td className="w-px whitespace-nowrap py-3.5 px-4 align-middle text-sm text-ui-text-secondary">
        {formatDateCompact(batch.created_at)}
      </td>

      {/* Nothing to review until the scan finishes. */}
      <td className="w-px py-3.5 px-4" />
    </tr>
  );
};

export default ScanBatchRunningRow;
